import React from 'react';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartCard } from './ChartCard';
import { colors } from '../../theme/tokens';
import { DEMO_OPPONENTS } from './demoData';

export const OpponentCountryCard: React.FC = () => (
  <ChartCard
    title="Comparaisons par pays avec les concurrents (2026)"
    subtitle="Client vs Concurrents par pays"
  >
    <OpponentList>
      {DEMO_OPPONENTS.map((opponent) => {
        const chartData = opponent.countries.map((c) => ({
          country: c.country,
          'Client A': c.client,
          [opponent.name]: c.opponent
        }));

        return (
          <OpponentSection key={opponent.name}>
            <OpponentTitle>Client A vs {opponent.name}</OpponentTitle>

            <ChartWrap>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                  <XAxis
                    dataKey="country"
                    tick={{ fontSize: 11, fill: colors.inkMuted }}
                  />
                  <YAxis
                    width={40}
                    tick={{ fontSize: 11, fill: colors.inkMuted }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Client A" fill={colors.indigo} />
                  <Bar dataKey={opponent.name} fill={colors.crimson} />
                </BarChart>
              </ResponsiveContainer>
            </ChartWrap>

            <ComparisonGrid>
              {opponent.countries.map((c) => (
                <ComparisonCell key={c.country} $winning={c.diff > 0}>
                  <CellCountry>{c.country}</CellCountry>
                  <CellRow>
                    <CellSide>
                      <CellSideLabel>Client A</CellSideLabel>
                      <CellSideValue>{c.client}</CellSideValue>
                    </CellSide>
                    <CellDiff $positive={c.diff > 0}>
                      {c.diff > 0 ? '+' : ''}
                      {c.diff}
                    </CellDiff>
                    <CellSide>
                      <CellSideLabel>{opponent.name}</CellSideLabel>
                      <CellSideValue>{c.opponent}</CellSideValue>
                    </CellSide>
                  </CellRow>
                </ComparisonCell>
              ))}
            </ComparisonGrid>
          </OpponentSection>
        );
      })}
    </OpponentList>
  </ChartCard>
);

// --- Styled ---

const OpponentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const OpponentSection = styled.div``;

const OpponentTitle = styled.h4`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 12px;
`;

const ChartWrap = styled.div``;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const ComparisonCell = styled.div<{ $winning: boolean }>`
  padding: 12px;
  border-radius: var(--radius-sm);
  border: 1px solid
    ${(p) => (p.$winning ? colors.indigoSoft : colors.crimsonSoft)};
  background: ${(p) => (p.$winning ? colors.indigoSoft : colors.crimsonSoft)};
`;

const CellCountry = styled.p`
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  margin: 0 0 8px;
`;

const CellRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CellSide = styled.div`
  text-align: center;
`;

const CellSideLabel = styled.p`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--ink-muted);
  margin: 0;
`;

const CellSideValue = styled.p`
  font-family: var(--font-mono);
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  margin: 2px 0 0;
`;

const CellDiff = styled.span<{ $positive: boolean }>`
  font-family: var(--font-mono);
  font-size: 18px;
  font-weight: 700;
  color: ${(p) => (p.$positive ? colors.indigo : colors.crimson)};
`;
